import { useState } from 'react'

import type { PerformanceLine, ReportDimension } from '@/api/reports-types'
import type { Branch } from '@/api/types'
import { MoneyDisplay } from '@/components/money-display'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ErrorSurface } from '@/components/error-surface'

import { ReportExportButtons } from './report-export-buttons'
import {
  DIMENSION_LABEL,
  REPORT_DIMENSIONS,
  formatRatio,
  performanceExportReport,
  resolveBranchLabel,
  startOfMonth,
  today,
} from './constants'
import { BranchFilterField, LoanOfficerFilterField } from './filters'
import { REPORTS_PERMISSIONS, useReportsPermissions } from './permissions'
import { usePerformance, useReportsBranchReach } from './queries'
import { ReportHeaderNotice } from './report-header-notice'
import { RestrictedPanel } from './restricted-panel'

/**
 * Officer, branch and group performance.
 *
 * One endpoint, sliced by whichever `ReportDimension` is chosen — this screen does not have five
 * different reads behind it, it has one filter that changes what a row means. Portfolio figures are
 * as at `asOf`; collection and disbursement figures cover the period, which is why the header
 * states both ranges rather than one shared one.
 *
 * Every dimension exports, in every format — the catalogue gained `BranchPerformance`,
 * `LoanProductPerformance` and `InstitutionPerformance` alongside the two it always had. See
 * `performanceExportReport`.
 */
export function PerformanceSection() {
  const permissions = useReportsPermissions()
  const reach = useReportsBranchReach()

  const [asOf, setAsOf] = useState(today)
  const [fromDate, setFromDate] = useState(() => startOfMonth(today()))
  const [toDate, setToDate] = useState(today)
  const [dimension, setDimension] = useState<ReportDimension>('Branch')
  const [branchId, setBranchId] = useState<string | null>(null)
  const [loanOfficerId, setLoanOfficerId] = useState<string | null>(null)

  const query = usePerformance(
    { asOf, fromDate, toDate, dimension, branchId, loanOfficerId },
    permissions.canViewPerformance && asOf !== '' && fromDate !== '' && toDate !== '',
  )

  if (!permissions.canViewPerformance) {
    return (
      <RestrictedPanel what="Performance" permission={REPORTS_PERMISSIONS.officerPerformanceView} />
    )
  }

  const exportReport = performanceExportReport(dimension)

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="grid gap-3 p-4 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor="performance-as-of">Portfolio as of</Label>
            <Input
              id="performance-as-of"
              type="date"
              value={asOf}
              onChange={(event) => setAsOf(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="performance-from">Activity from</Label>
            <Input
              id="performance-from"
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="performance-to">Activity to</Label>
            <Input
              id="performance-to"
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="performance-dimension">Break down by</Label>
            <Select value={dimension} onValueChange={(next) => setDimension(next as ReportDimension)}>
              <SelectTrigger id="performance-dimension">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REPORT_DIMENSIONS.map((candidate) => (
                  <SelectItem key={candidate} value={candidate}>
                    {DIMENSION_LABEL[candidate]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <BranchFilterField
            id="performance-branch"
            label="Scope to branch"
            branchId={branchId}
            onChange={(next) => {
              setBranchId(next)
              setLoanOfficerId(null)
            }}
          />
          <LoanOfficerFilterField
            id="performance-officer"
            label="Scope to officer"
            branchId={branchId}
            loanOfficerId={loanOfficerId}
            onChange={setLoanOfficerId}
          />
        </CardContent>
      </Card>

      {query.isPending ? (
        <div className="space-y-3">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : query.isError ? (
        <ErrorSurface error={query.error} onRetry={() => void query.refetch()} title="Performance could not be read" />
      ) : query.data ? (
        <>
          <ReportHeaderNotice header={query.data.header} />

          <ReportExportButtons
            query={{ report: exportReport, asOf, fromDate, toDate, dimension, branchId, loanOfficerId }}
            totalRows={query.data.lines.length}
          />

          <PerformanceTotalCard total={query.data.total} />
          <PerformanceTable lines={query.data.lines} branches={reach.branches} />
        </>
      ) : null}
    </div>
  )
}

function PerformanceTotalCard({ total }: { total: PerformanceLine }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{DIMENSION_LABEL[total.dimension]} total</CardTitle>
        <Badge tone="neutral">{total.activeFacilities} active facilities</Badge>
      </CardHeader>
      <CardContent className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Portfolio outstanding" money={total.portfolioOutstanding} />
        <Stat label="Portfolio at risk" money={total.portfolioAtRisk} />
        <Stat label="At-risk ratio" value={formatRatio(total.atRiskRatio)} />
        <Stat label="Non-performing ratio" value={formatRatio(total.nonPerformingRatio)} />
        <Stat label="Expected in period" money={total.expectedInPeriod} />
        <Stat label="Collected in period" money={total.collectedInPeriod} />
        <Stat label="Collection rate" value={formatRatio(total.collectionRate)} />
        <Stat label="Disbursed in period" money={total.disbursedInPeriod} />
      </CardContent>
    </Card>
  )
}

function Stat({
  label,
  money,
  value,
}: {
  label: string
  money?: { minor: number; currency: string }
  value?: string
}) {
  return (
    <div className="rounded-control border border-border-subtle bg-surface p-3">
      <p className="text-2xs text-muted">{label}</p>
      {money ? (
        <MoneyDisplay value={money.minor} currency={money.currency} emphasis="strong" className="text-left" />
      ) : (
        <p className="numeric text-sm font-semibold tabular-nums text-heading">{value}</p>
      )}
    </div>
  )
}

function PerformanceTable({
  lines,
  branches,
}: {
  lines: PerformanceLine[]
  branches: Branch[]
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>By {lines[0] ? DIMENSION_LABEL[lines[0].dimension].toLowerCase() : 'dimension'}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {lines.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-body">Nothing in this scope.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <tr>
                  <TableHead scope="col">Member</TableHead>
                  <TableHead scope="col" className="text-right">
                    Facilities
                  </TableHead>
                  <TableHead scope="col" className="text-right">
                    Outstanding
                  </TableHead>
                  <TableHead scope="col" className="text-right">
                    At-risk ratio
                  </TableHead>
                  <TableHead scope="col" className="text-right">
                    Expected
                  </TableHead>
                  <TableHead scope="col" className="text-right">
                    Collected
                  </TableHead>
                  <TableHead scope="col" className="text-right">
                    Collection rate
                  </TableHead>
                  <TableHead scope="col" className="text-right">
                    Disbursed
                  </TableHead>
                </tr>
              </TableHeader>
              <TableBody>
                {lines.map((line) => (
                  <TableRow key={line.dimensionKey}>
                    <TableCell className="text-sm font-medium text-heading">
                      {line.dimension === 'Branch'
                        ? resolveBranchLabel(branches, line.dimensionKey)
                        : line.dimensionLabel}
                    </TableCell>
                    <TableCell className="numeric text-right tabular-nums">{line.activeFacilities}</TableCell>
                    <TableCell className="text-right">
                      <MoneyDisplay value={line.portfolioOutstanding.minor} currency={line.portfolioOutstanding.currency} symbol={false} />
                    </TableCell>
                    <TableCell className="numeric text-right tabular-nums">{formatRatio(line.atRiskRatio)}</TableCell>
                    <TableCell className="text-right">
                      <MoneyDisplay value={line.expectedInPeriod.minor} currency={line.expectedInPeriod.currency} symbol={false} />
                    </TableCell>
                    <TableCell className="text-right">
                      <MoneyDisplay value={line.collectedInPeriod.minor} currency={line.collectedInPeriod.currency} symbol={false} />
                    </TableCell>
                    <TableCell className="numeric text-right tabular-nums">{formatRatio(line.collectionRate)}</TableCell>
                    <TableCell className="text-right">
                      <MoneyDisplay value={line.disbursedInPeriod.minor} currency={line.disbursedInPeriod.currency} symbol={false} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
